import React from 'react';
import { Assessment } from 'pages/Search/models';
import {
  Row,
  Col,
  Divider,
  Tooltip,
  Checkbox,
  Descriptions,
  Empty,
} from 'antd';
import {
  formatPriceString,
  tabSelector,
  getDataTypeName,
  formatSize,
} from 'common';
import { useDispatch, useSelector } from 'react-redux';
import { searchIsSelectedUpdate } from 'pages/Search';

const Assesments: React.FC<{
  assessment: Assessment;
  searchCriteria: string;
  resultItemIndex: number;
}> = ({
  assessment,
  assessment: { guid, documentType },
  searchCriteria,
  resultItemIndex,
}) => {
  const dispatch = useDispatch();
  const tabName = useSelector(tabSelector);
  const { transitionalValues, actualValues, marketValue, hasValues } =
    assessment;

  const onChange = ({ target: { checked } }: any) => {
    dispatch(
      searchIsSelectedUpdate(
        getDataTypeName(tabName),
        documentType,
        guid,
        checked,
        resultItemIndex,
      ),
    );
  };

  return (
    <>
      {hasValues ? (
        <>
          <Tooltip title={'Check for export'}>
            <Checkbox onChange={onChange} defaultChecked={true} />
          </Tooltip>
          <Divider dashed />
          <Descriptions
            title="Property Information"
            bordered
            size={'small'}
            column={5}
          >
            <Descriptions.Item label="County">
              {assessment.propertyInformation.county}
            </Descriptions.Item>
            <Descriptions.Item label="Section">
              {assessment.propertyInformation.sec}
            </Descriptions.Item>
            <Descriptions.Item label="Volume">
              {assessment.propertyInformation.vol}
            </Descriptions.Item>
            <Descriptions.Item label="Block">
              {parseInt(assessment.propertyInformation.block)}
            </Descriptions.Item>
            <Descriptions.Item label="Lot">
              {parseInt(assessment.propertyInformation.lot)}
            </Descriptions.Item>
            <Descriptions.Item label="Easement">
              {assessment.propertyInformation.easement}
            </Descriptions.Item>

            <Descriptions.Item label="Address" span={2}>
              {assessment.propertyInformation.address}
            </Descriptions.Item>
            <Descriptions.Item label="Owner" span={2}>
              {assessment.propertyInformation.owner}
            </Descriptions.Item>
            <Descriptions.Item label="Apt No">
              {assessment.propertyInformation.apartmentNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Att. Group">
              {assessment.propertyInformation.attorneyGroup}
            </Descriptions.Item>
            <Descriptions.Item label="Exemptions">
              {assessment.propertyInformation.exemptions}
            </Descriptions.Item>
            <Descriptions.Item label="Zoning">
              {assessment.propertyInformation.zoning}
            </Descriptions.Item>
            <Descriptions.Item label="Reuc">
              {assessment.propertyInformation.reuc}
            </Descriptions.Item>
            <Descriptions.Item label="Tax Class">
              {assessment.propertyInformation.taxClass}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions
            title="Building Information"
            bordered
            size={'small'}
            column={4}
          >
            <Descriptions.Item label="Building Class">
              {assessment.buildingInformation.buildingClass}
            </Descriptions.Item>
            <Descriptions.Item label="Gar">
              {assessment.buildingInformation.gar}
            </Descriptions.Item>
            <Descriptions.Item label="Year Built">
              {assessment.buildingInformation.yearBuilt}
            </Descriptions.Item>
            <Descriptions.Item label="Apts">
              {assessment.buildingInformation.coopApts}
            </Descriptions.Item>
            <Descriptions.Item label="Stories" span={2}>
              {assessment.buildingInformation.stories}
            </Descriptions.Item>
            <Descriptions.Item label="Buildings">
              {assessment.buildingInformation.buildings}
            </Descriptions.Item>
            <Descriptions.Item label="Total Units">
              {assessment.buildingInformation.totalUnits}
            </Descriptions.Item>
            <Descriptions.Item label="Lot Frontage" span={2}>
              {assessment.buildingInformation.lotFrontage}
            </Descriptions.Item>
            <Descriptions.Item label="Lot Depth">
              {assessment.buildingInformation.lotDepth}
            </Descriptions.Item>
            <Descriptions.Item label="Apartment Number">
              {assessment.buildingInformation.apartmentNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Building Frontage" span={2}>
              {assessment.buildingInformation.buildingFrontage}
            </Descriptions.Item>
            <Descriptions.Item label="Building Depth">
              {assessment.buildingInformation.buildingDepth}
            </Descriptions.Item>
            <Descriptions.Item label="Condo Number">
              {assessment.buildingInformation.condoNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Corner Status" span={2}>
              {assessment.buildingInformation.cornerStatus}
            </Descriptions.Item>
            <Descriptions.Item label="Irregular">
              {assessment.buildingInformation.irregular}
            </Descriptions.Item>
            <Descriptions.Item label="Market value">
              {formatPriceString(marketValue)}
            </Descriptions.Item>
          </Descriptions>
          <Row type="flex" gutter={10}>
            <Col span={12}>
              <Descriptions
                title="Transitional Values"
                bordered
                size={'small'}
                column={1}
              >
                <Descriptions.Item label="Assessed Land">
                  {formatSize(transitionalValues.assessedLand)}
                </Descriptions.Item>
                <Descriptions.Item label="Assessed Total">
                  {formatSize(transitionalValues.assessedTotal)}
                </Descriptions.Item>
                <Descriptions.Item label="Exempt Land">
                  {formatSize(transitionalValues.exemptLand)}
                </Descriptions.Item>
                <Descriptions.Item label="Exempt Total">
                  {formatSize(transitionalValues.exemptTotal)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={12}>
              <Descriptions
                title="Actual Values"
                bordered
                size={'small'}
                column={1}
              >
                <Descriptions.Item label="Assessed Land">
                  {formatSize(actualValues.assessedLand)}
                </Descriptions.Item>
                <Descriptions.Item label="Assessed Total">
                  {formatSize(actualValues.assessedTotal)}
                </Descriptions.Item>
                <Descriptions.Item label="Exempt Land">
                  {formatSize(actualValues.exemptLand)}
                </Descriptions.Item>
                <Descriptions.Item label="Exempt Total">
                  {formatSize(actualValues.exemptTotal)}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </>
      ) : (
        <Row type="flex" justify="center" align="middle">
          <Empty
            description={`No data found for Assessment,${
              searchCriteria &&
              // @ts-ignore:
              searchCriteria.match(/;(.*)/)[1]
            }`}
          />
        </Row>
      )}
    </>
  );
};

export default React.memo(Assesments);
